import { URLSearchParams as NodeURLSearchParams } from "url";
import { encode } from "./lib/redirect.js";
import { Url as defaultUrls } from "./defaults.js";
import { v4 as uuid } from "uuid"; // uuid@8.x
//#region config
export const Config = {
    github: {
        base: "https://api.github.com",
        scope: "user:email",
    },
    google: {
        scope: ["https://www.googleapis.com/auth/userinfo.email"],
    },
};
//#endregion
//#region screens
export const AppName = "auth"; // i.e. url = /auth/#/screen-route/step/data
// keep in sync with:
// - ScreenRoute
// - FlowOrder
// - /opt/spike/v9/priv/stack/app.spikedata.co.za/src/ux/auth/src/routes.ts
export var ScreenName;
(function (ScreenName) {
    ScreenName["SignIn"] = "SignIn";
    ScreenName["SignUp"] = "SignUp";
    ScreenName["VerifyLocalEmail"] = "VerifyLocalEmail";
    ScreenName["TeamCreate"] = "TeamCreate";
    ScreenName["TeamInvite"] = "TeamInvite";
    ScreenName["AcceptInvite"] = "AcceptInvite";
    ScreenName["ForgotPasswordStart"] = "ForgotPasswordStart";
    ScreenName["ForgotPasswordVerify"] = "ForgotPasswordVerify";
    ScreenName["ForgotPasswordSetPassword"] = "ForgotPasswordSetPassword";
    ScreenName["ForgotPasswordComplete"] = "ForgotPasswordComplete";
    ScreenName["Locked"] = "Locked";
    ScreenName["Test"] = "Test";
})(ScreenName || (ScreenName = {}));
// keep in sync with:
// - ScreenName
export var ScreenRoute;
(function (ScreenRoute) {
    ScreenRoute["SignIn"] = "/sign-in";
    ScreenRoute["SignUp"] = "/sign-up";
    ScreenRoute["VerifyLocalEmail"] = "/verify-email";
    ScreenRoute["TeamCreate"] = "/team-create";
    ScreenRoute["TeamInvite"] = "/team-invite";
    ScreenRoute["AcceptInvite"] = "/accept-invite";
    ScreenRoute["ForgotPasswordStart"] = "/forgot-start";
    ScreenRoute["ForgotPasswordVerify"] = "/forgot-verify";
    ScreenRoute["ForgotPasswordSetPassword"] = "/forgot-set-password";
    ScreenRoute["ForgotPasswordComplete"] = "/forgot-complete";
    ScreenRoute["Locked"] = "/locked";
    ScreenRoute["Test"] = "/test";
})(ScreenRoute || (ScreenRoute = {}));
// Enumerates all possible flows = sequence of auth screens
// keep in sync with FlowOrder
export var Flow;
(function (Flow) {
    Flow["signUp_local"] = "signUp_local";
    Flow["signUp_oauth"] = "signUp_oauth";
    Flow["acceptInvite"] = "acceptInvite";
    Flow["signIn_local_complete"] = "signIn_local_complete";
    Flow["signIn_local_incomplete_email"] = "signIn_local_incomplete_email";
    Flow["signIn_local_incomplete_team"] = "signIn_local_incomplete_team";
    Flow["signIn_oauth_complete"] = "signIn_oauth_complete";
    Flow["signIn_oauth_incomplete"] = "signIn_oauth_incomplete";
    Flow["forgotPass"] = "forgotPass";
    Flow["locked"] = "locked";
})(Flow || (Flow = {}));
// keep in sync with Flow
// What screens must be complete before screen can mount - see `Nav.startFlow(..)` in ./src/ux/auth code
export const FlowOrder = {
    signUp_local: [ScreenName.SignUp, ScreenName.VerifyLocalEmail, ScreenName.TeamCreate, ScreenName.TeamInvite],
    signUp_oauth: [ScreenName.SignUp, ScreenName.TeamCreate, ScreenName.TeamInvite],
    acceptInvite: [ScreenName.AcceptInvite],
    signIn_local_complete: [ScreenName.SignIn],
    signIn_local_incomplete_email: [
        ScreenName.SignIn,
        ScreenName.VerifyLocalEmail,
        ScreenName.TeamCreate,
        ScreenName.TeamInvite,
    ],
    signIn_local_incomplete_team: [ScreenName.SignIn, ScreenName.TeamCreate, ScreenName.TeamInvite],
    signIn_oauth_complete: [ScreenName.SignIn],
    signIn_oauth_incomplete: [ScreenName.SignIn, ScreenName.TeamCreate, ScreenName.TeamInvite],
    forgotPass: [
        ScreenName.ForgotPasswordStart,
        ScreenName.ForgotPasswordVerify,
        ScreenName.ForgotPasswordSetPassword,
        ScreenName.ForgotPasswordComplete,
    ],
    locked: [], // can get here in multiple ways - VerifyLocalEmail or signIn {local|oauth}
};
//#endregion
//#region request and response types + result codes
export var GenericResult;
(function (GenericResult) {
    GenericResult[GenericResult["Success"] = 0] = "Success";
    GenericResult[GenericResult["Fail"] = -1] = "Fail";
})(GenericResult || (GenericResult = {}));
export var LocalSignInResult;
(function (LocalSignInResult) {
    LocalSignInResult[LocalSignInResult["Success"] = 0] = "Success";
    LocalSignInResult[LocalSignInResult["Fail"] = -1] = "Fail";
    LocalSignInResult[LocalSignInResult["IncompleteSignUp"] = -2] = "IncompleteSignUp";
    LocalSignInResult[LocalSignInResult["Locked"] = -3] = "Locked";
})(LocalSignInResult || (LocalSignInResult = {}));
export var LocalSignInCallbackResult;
(function (LocalSignInCallbackResult) {
    LocalSignInCallbackResult[LocalSignInCallbackResult["Success"] = 0] = "Success";
    // Actual reasons, don't expose to caller=
    LocalSignInCallbackResult[LocalSignInCallbackResult["Exception"] = -1] = "Exception";
    LocalSignInCallbackResult[LocalSignInCallbackResult["UserNotFound"] = -2] = "UserNotFound";
    LocalSignInCallbackResult[LocalSignInCallbackResult["PasswordMissing"] = -3] = "PasswordMissing";
    LocalSignInCallbackResult[LocalSignInCallbackResult["PasswordIncorrect"] = -4] = "PasswordIncorrect";
})(LocalSignInCallbackResult || (LocalSignInCallbackResult = {}));
export function create_LocalSignInRes_IncompleteSignUp(incompleteStep) {
    return {
        result: LocalSignInResult.IncompleteSignUp,
        data: {
            incompleteStep,
        },
    };
}
export var OauthSignInResult;
(function (OauthSignInResult) {
    OauthSignInResult["Success"] = "success";
    OauthSignInResult["Exception"] = "exception";
    OauthSignInResult["CantRetrieveEmail"] = "cant-retrieve-email";
    OauthSignInResult["UserNotFound"] = "user-not-found";
    OauthSignInResult["IncompleteSignUp"] = "incomplete-sign-up";
    OauthSignInResult["Locked"] = "locked";
})(OauthSignInResult || (OauthSignInResult = {}));
//#endregion
//#region SignUp
export var OauthSignUpResult;
(function (OauthSignUpResult) {
    OauthSignUpResult["Success"] = "success";
    OauthSignUpResult["Exception"] = "exception";
    OauthSignUpResult["CantRetrieveEmail"] = "cant-retrieve-email";
    OauthSignUpResult["UserAlreadyRegistered"] = "user-already-registered";
})(OauthSignUpResult || (OauthSignUpResult = {}));
export var OauthCallbackResult;
(function (OauthCallbackResult) {
    OauthCallbackResult[OauthCallbackResult["Exception"] = 1] = "Exception";
    OauthCallbackResult[OauthCallbackResult["CantRetrieveEmail"] = 2] = "CantRetrieveEmail";
    OauthCallbackResult[OauthCallbackResult["UserNotFound"] = 3] = "UserNotFound";
    OauthCallbackResult[OauthCallbackResult["FoundUser"] = 4] = "FoundUser";
})(OauthCallbackResult || (OauthCallbackResult = {}));
export var LocalSignUpResult;
(function (LocalSignUpResult) {
    LocalSignUpResult[LocalSignUpResult["Success"] = 0] = "Success";
    LocalSignUpResult[LocalSignUpResult["Fail"] = -1] = "Fail";
    LocalSignUpResult[LocalSignUpResult["UserAlreadyRegistered"] = -2] = "UserAlreadyRegistered";
})(LocalSignUpResult || (LocalSignUpResult = {}));
export var LocalSignUpCallbackResult;
(function (LocalSignUpCallbackResult) {
    LocalSignUpCallbackResult[LocalSignUpCallbackResult["Success"] = 0] = "Success";
    // Actual reasons, don't expose to caller=
    LocalSignUpCallbackResult[LocalSignUpCallbackResult["Exception"] = -1] = "Exception";
    LocalSignUpCallbackResult[LocalSignUpCallbackResult["PasswordMissing"] = -2] = "PasswordMissing";
    LocalSignUpCallbackResult[LocalSignUpCallbackResult["UserAlreadyRegistered"] = -3] = "UserAlreadyRegistered";
})(LocalSignUpCallbackResult || (LocalSignUpCallbackResult = {}));
export var VerifyResult;
(function (VerifyResult) {
    VerifyResult[VerifyResult["Success"] = 0] = "Success";
    VerifyResult[VerifyResult["Fail"] = -1] = "Fail";
    VerifyResult[VerifyResult["TooManyFails"] = -2] = "TooManyFails";
})(VerifyResult || (VerifyResult = {}));
export var TeamCreateResult;
(function (TeamCreateResult) {
    TeamCreateResult[TeamCreateResult["Success"] = 0] = "Success";
    TeamCreateResult[TeamCreateResult["Fail"] = -1] = "Fail";
    TeamCreateResult[TeamCreateResult["AlreadyRegistered"] = -2] = "AlreadyRegistered";
})(TeamCreateResult || (TeamCreateResult = {}));
export var TeamInviteResult;
(function (TeamInviteResult) {
    TeamInviteResult[TeamInviteResult["Success"] = 0] = "Success";
    TeamInviteResult[TeamInviteResult["Fail"] = -1] = "Fail";
    TeamInviteResult[TeamInviteResult["AlreadyRegistered"] = -2] = "AlreadyRegistered";
})(TeamInviteResult || (TeamInviteResult = {}));
// keep in sync with @db/src/lib/auth2.ts : RemoveTeamMemberResult
export var TeamRemoveResult;
(function (TeamRemoveResult) {
    TeamRemoveResult[TeamRemoveResult["Success"] = 0] = "Success";
    TeamRemoveResult[TeamRemoveResult["Fail"] = 1] = "Fail";
    TeamRemoveResult[TeamRemoveResult["NotOnTheTeam"] = 2] = "NotOnTheTeam";
})(TeamRemoveResult || (TeamRemoveResult = {}));
//#endregion
//#endregion
//#region other
export var Strategy;
(function (Strategy) {
    Strategy["local"] = "local";
    Strategy["google"] = "google";
    Strategy["github"] = "github";
})(Strategy || (Strategy = {}));
export var Sign;
(function (Sign) {
    Sign["in"] = "in";
    Sign["up"] = "up";
})(Sign || (Sign = {}));
//#endregion
export const Url = {
    // static urls
    server: {
        local: {
            // local = uses ajax requests, no serverRedirects required
            signIn: "/api/auth/local/sign-in/",
            signUp: "/api/auth/local/sign-up/",
            verify: "/api/auth/local/verify/",
        },
        github: {
            signIn: "/api/auth/github/sign-in/",
            signUp: "/api/auth/github/sign-up/",
            callback: "/api/auth/github/callback/",
        },
        google: {
            signIn: "/api/auth/google/sign-in/",
            signUp: "/api/auth/google/sign-up/",
            callback: "/api/auth/google/callback/",
        },
        // used by local, github and google sign-up flows
        shared: {
            signOut: "/api/auth/sign-out/",
            teamCreate: "/api/auth/team/create/",
            teamInvite: "/api/auth/team/invite/",
            teamRemove: "/api/auth/team/remove/",
            acceptInvite: {
                clickLink: "/api/auth/accept-invite/:token",
                setPassword: "/api/auth/accept-invite/set-password/",
            },
            accessToken: "/api/auth/access-token/",
            currentUser: "/api/auth/current-user/",
            forgotPassword: {
                sendReset: "/api/auth/forgot/send-reset/",
                verify: "/api/auth/forgot/verify/",
                setPassword: "/api/auth/forgot/set-password/",
            },
        },
        test: {
            user: "/api/auth/test/user",
            session: "/api/auth/test/session",
        },
    },
    // the following server-side routes have to return data to UX via redirects
    serverRedirect: {
        github: {
            signIn: {
                // keys = OauthSignInResult
                Success: defaultUrls.signIn,
                Exception: encode(AppName, ScreenRoute.SignIn, OauthSignInResult.Exception),
                CantRetrieveEmail: encode(AppName, ScreenRoute.SignIn, OauthSignInResult.CantRetrieveEmail),
                UserNotFound: (email) => encode(AppName, ScreenRoute.SignIn, OauthSignInResult.UserNotFound, { email }),
                IncompleteSignUp: encode(AppName, ScreenRoute.SignIn, OauthSignInResult.IncompleteSignUp),
                Locked: encode(AppName, ScreenRoute.Locked),
            },
            signUp: {
                // keys = OauthSignUpResult
                Success: (email) => {
                    const data = { email, strategy: Strategy.github };
                    return encode(AppName, ScreenRoute.SignUp, // return to signup screen for consistency, makes Nav.startFlow() easier. UX will navigate to next screen = TeamCreate.
                    OauthSignUpResult.Success, 
                    //
                    data);
                },
                Exception: encode(AppName, ScreenRoute.SignUp, OauthSignUpResult.Exception),
                CantRetrieveEmail: encode(AppName, ScreenRoute.SignUp, OauthSignUpResult.CantRetrieveEmail),
                UserAlreadyRegistered: (email) => {
                    const data = {
                        provider: Strategy.github,
                        email,
                    };
                    return encode(AppName, ScreenRoute.SignUp, OauthSignUpResult.UserAlreadyRegistered, data);
                },
            },
        },
        google: {
            signIn: {
                // keys = OauthSignInResult
                Success: defaultUrls.signIn,
                Exception: encode(AppName, ScreenRoute.SignIn, OauthSignInResult.Exception),
                CantRetrieveEmail: encode(AppName, ScreenRoute.SignIn, OauthSignInResult.CantRetrieveEmail),
                UserNotFound: (email) => encode(AppName, ScreenRoute.SignIn, OauthSignInResult.UserNotFound, { email }),
                IncompleteSignUp: encode(AppName, ScreenRoute.SignIn, OauthSignInResult.IncompleteSignUp),
                Locked: encode(AppName, ScreenRoute.Locked),
            },
            signUp: {
                // keys = OauthSignUpResult
                Success: (email) => {
                    const data = { email, strategy: Strategy.google };
                    return encode(AppName, ScreenRoute.SignUp, // return to signup screen for consistency, makes Nav.startFlow() easier. UX will navigate to next screen = TeamCreate.
                    OauthSignUpResult.Success, 
                    //
                    data);
                },
                Exception: encode(AppName, ScreenRoute.SignUp, OauthSignUpResult.Exception),
                CantRetrieveEmail: encode(AppName, ScreenRoute.SignUp, OauthSignUpResult.CantRetrieveEmail),
                UserAlreadyRegistered: (email) => {
                    const data = {
                        provider: Strategy.google,
                        email,
                    };
                    return encode(AppName, ScreenRoute.SignUp, OauthSignUpResult.UserAlreadyRegistered, data);
                },
            },
        },
        shared: {
            signOut: defaultUrls.signOut,
            acceptInvite: {
                ux: (email) => {
                    const data = {
                        email,
                    };
                    return encode(AppName, ScreenRoute.AcceptInvite, undefined, data);
                },
            },
        },
    },
    serverHelper: {
        shared: {
            teamInvite: {
                createLink: (token) => `/api/auth/accept-invite/${token}`, // must match: server.shared.acceptInvite.clickLink
            },
        },
    },
};
// see: isIncompleteSignUp
export const IncompleteAllowed = [
    // even if sign-up isIncompleteSignUp, you can still access these urls - which are protected by routes.ensureLoggedInExpress()
    Url.server.local.verify,
    Url.server.shared.teamCreate,
    Url.server.shared.teamInvite,
    Url.server.shared.teamRemove,
];
export function makeAuthReturnUrl(url, isExpress = false, debug = false) {
    // queryString
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const U = typeof URLSearchParams !== "undefined" ? URLSearchParams : NodeURLSearchParams; // hack isomorphic
    const search = new U();
    search.append("returnUrl", url);
    const redirectToUrlBase = "/auth/";
    const queryString = search.toString();
    let redirectToUrl = redirectToUrlBase + "?" + queryString;
    // hash
    redirectToUrl += "#" + ScreenRoute.SignIn;
    /* debug express-session */
    if (debug) {
        const t = isExpress ? "ensureLoggedInExpress" : "ensureLoggedInStatic";
        console.log(`${t}: ${url}: redirectTo ${redirectToUrl}`);
        // debugSession(req);
    }
    /* */
    return redirectToUrl;
}
export function generateInviteLink(baseUrl) {
    const token = uuid() + uuid();
    const route = Url.serverHelper.shared.teamInvite.createLink(token);
    return {
        link: baseUrl + route,
        token,
    };
}
//# sourceMappingURL=auth.js.map